import { FunctionComponent, ReactElement, useMemo, useEffect, useRef } from 'react';
import FormDialog from '@/components/ui/form-dialog';
import { Customer, User } from '@/core/entities';
import { useCreateUser, useRoles, useRolesHierarchy, useUpdateUser } from '@/queries/users';
import { Roles, UserStatus } from '@/core/types';
import { USER_FORM_KEY } from '@/shared/constants/form-keys';
import { CustomerSiteOption } from '@/api/customers/types';
import { pushToDataLayer, ContentGroup } from '@/shared/utils/analytics';
import { Typography } from '@mui/material';
import { UserFormModel } from './types';
import { SiteSelectType } from './site-select/types';
import UserFormFields from './UserFormFields';

interface UserFormProps {
	formModel: UserFormModel;
	updateFormModel: (value: UserFormModel) => void;
	open: boolean;
	onClose: () => void;
	disabled?: boolean;
	isAdminForm?: boolean;
	user?: User;
	customer?: Customer | null;
	onSubmitted?: () => Promise<void> | void;
	sites?: CustomerSiteOption[];
}

const UserForm: FunctionComponent<UserFormProps> = ({
	formModel,
	updateFormModel,
	open,
	onClose,
	disabled,
	isAdminForm,
	user,
	customer,
	onSubmitted,
	sites,
}): ReactElement => {
	const prevEmailRef = useRef<string | undefined>(undefined);
	const prevRoleId = useRef<string | undefined>(undefined);

	const { data: roles } = useRoles();
	const { data: rolesHierarchy } = useRolesHierarchy({ enabled: !isAdminForm });
	const roleOptions = useMemo(
		() => rolesHierarchy?.filter((role) => role.title !== Roles.LEVIANT_ADMIN),
		[rolesHierarchy]
	);

	const adminId = useMemo(
		() => roles?.find((role) => role.title === Roles.LEVIANT_ADMIN)?.id,
		[roles]
	);
	const operatorId = useMemo(
		() => roles?.find((role) => role.title === Roles.OPERATOR)?.id,
		[roles]
	);
	const siteSupervisorId = useMemo(
		() => roles?.find((role) => role.title === Roles.SITE_SUPERVISOR)?.id,
		[roles]
	);

	const siteFieldType = useMemo(
		() =>
			formModel.roleId === siteSupervisorId ? SiteSelectType.SELECT : SiteSelectType.MULTISELECT,
		[formModel.roleId, siteSupervisorId]
	);

	const isEmailDisabled: boolean = useMemo(
		() => (user && user.status === UserStatus.ACTIVE) || formModel.roleId === operatorId,
		[user, formModel.roleId, operatorId]
	);

	const title = useMemo(() => (user ? 'Edit user' : 'Create new user'), [user]);
	const submitLabel = useMemo(() => (user ? 'Save' : 'Create user'), [user]);

	const trackCreateUser = () => {
		/* eslint-disable */
		const createdUserEventProps = isAdminForm
			? {
					event: 'admin_user_created',
					content_group: ContentGroup.Users,
			  }
			: {
					event: 'user_created',
					content_group: ContentGroup.Customers,
					customer_name: customer?.name,
					created_user_role: roleOptions?.find((roleOption) => roleOption.id === formModel.roleId)
						?.title,
			  };
		/* eslint-enable */
		pushToDataLayer(createdUserEventProps);
	};

	const trackEditUser = () => {
		/* eslint-disable */
		const updatedUserEventProps = isAdminForm
			? {
					event: 'admin_user_edited',
					content_group: ContentGroup.Users,
			  }
			: {
					event: 'user_edited',
					content_group: ContentGroup.Customers,
					customer_name: customer?.name,
			  };
		/* eslint-enable */
		pushToDataLayer(updatedUserEventProps);
	};

	const { isLoading: isCreateUserFormSubmitting, mutate: onCreateUserFormSubmit } = useCreateUser({
		onSuccess: async () => {
			await onSubmitted?.();
			onClose();
			trackCreateUser();
		},
	});

	const { isLoading: isUpdateUserFormSubmitting, mutate: onUpdateUserFormSubmit } = useUpdateUser({
		onSuccess: async () => {
			await onSubmitted?.();
			onClose();
			trackEditUser();
		},
	});

	const onSubmit = () => {
		const roleId = isAdminForm ? adminId : formModel.roleId;
		if (!roleId) return;

		const request = {
			request: { ...formModel, roleId, customerId: customer?.id },
			formKey: USER_FORM_KEY,
		};
		if (user) {
			onUpdateUserFormSubmit({ id: user.id, request });
		} else {
			onCreateUserFormSubmit({ request });
		}
	};

	useEffect(() => {
		if (isAdminForm || siteFieldType === SiteSelectType.MULTISELECT) return;

		const { sites: selectedSites } = formModel;
		if (selectedSites?.length && selectedSites.length > 1) {
			updateFormModel({ ...formModel, sites: [] });
		}
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [siteFieldType, isAdminForm]);

	useEffect(() => {
		if (formModel.roleId === operatorId) {
			updateFormModel({ ...formModel, email: undefined });
		}
		if (prevRoleId.current === operatorId && prevEmailRef.current) {
			updateFormModel({ ...formModel, email: prevEmailRef.current });
		}
		prevRoleId.current = formModel.roleId;
		prevEmailRef.current = formModel.email;
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [formModel.roleId, open]);

	useEffect(() => {
		if (!open) {
			prevRoleId.current = undefined;
			prevEmailRef.current = undefined;
		}
	}, [open]);

	return (
		<FormDialog
			formKey={USER_FORM_KEY}
			title={title}
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="xs"
			actions={{
				cancel: {
					label: 'Cancel',
				},
				confirm: {
					label: submitLabel,
					disabled,
					isLoading: isCreateUserFormSubmitting || isUpdateUserFormSubmitting,
				},
			}}
			onSubmit={onSubmit}
		>
			<UserFormFields
				formModel={formModel}
				updateFormModel={updateFormModel}
				isAdminForm={isAdminForm}
				isEmailDisabled={isEmailDisabled}
				roleOptions={roleOptions}
				siteField={{
					type: siteFieldType,
					siteOptions: sites,
				}}
				pin={user?.pin}
				isOperator={user?.roleId === roleOptions?.find((role) => role.title === Roles.OPERATOR)?.id}
				guidance={
					<Typography color="primary.main" variant="caption" textAlign="justify">
						To activate a new user, click the "Create User" button below. A welcome email will then
						be sent to the new user with instructions for activation and password setup.
					</Typography>
				}
			/>
		</FormDialog>
	);
};

export default UserForm;
